import { useEffect, useMemo, useRef, useState } from 'react';
import { getShared, runSharedQuery } from '../../services/shares';
import { useZafronBridge } from './useZafronBridge';

const RENDER_ORIGIN = (process.env.REACT_APP_ZAFRON_RENDER_ORIGIN || '').replace(/\/+$/, '');

/**
 * A dashboard shown to someone with a link and no account.
 *
 * The token stays here. It authenticates this page's own API calls and is
 * never put in the frame's URL — the frame runs HTML we do not control, and
 * anything in its location is readable by that HTML.
 *
 * Rendered bare by design: no chrome, no range picker. The spec records what
 * that costs, namely that nothing outside the frame tells a visitor the
 * content is author-written.
 */
export function SharedDashboard({ token }) {
  const frameRef = useRef(null);
  const [shared, setShared] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setShared(null);
    setFailed(false);
    getShared(token)
      .then((s) => {
        if (cancelled) return;
        // A response without `queries` is not a usable share — treating it
        // as unavailable (rather than letting the queries.map below throw
        // during render, with no error boundary above this route to catch
        // it) is truthful and keeps the failure path uniform.
        if (!s || !Array.isArray(s.queries)) {
          setFailed(true);
          return;
        }
        setShared(s);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [token]);

  // The hook authorizes a query name against `dashboard.manifest.queries`.
  // The share endpoint returns names only — deliberately, since the manifest
  // would leak the owner's device ids — so present them in the shape the hook
  // already checks rather than giving it a second branch to get wrong.
  const dashboard = useMemo(() => {
    if (!shared) return null;
    return {
      id: shared.slug,
      title: shared.title,
      version: shared.version,
      manifest: { queries: Object.fromEntries(shared.queries.map((n) => [n, {}])) },
    };
  }, [shared]);

  // The declaration is resolved server-side from the pinned manifest, so the
  // name is all this needs; the decl the hook passes is the empty placeholder.
  //
  // This must stay a stable reference across renders: the bridge lists
  // resolveQuery in its message-listener effect's dependency array, and a
  // fresh function identity on every render tears that effect down, whose
  // cleanup aborts every in-flight query — including the one that just
  // started on the very render its own setBusy() caused. Keying the useMemo
  // on `token` alone (not on `shared` or `dashboard`) keeps it stable across
  // every render except an actual token change.
  const resolveQuery = useMemo(
    () => (name, decl, timeRange, signal) => runSharedQuery(token, name, signal),
    [token]
  );

  const { height, onFrameLoad } = useZafronBridge({
    frameRef,
    dashboard,
    timeRange: '7d',
    theme: 'light',
    resolveQuery,
    shared: true,
  });

  if (!RENDER_ORIGIN) {
    return <div role="alert">Dashboards are not configured in this build.</div>;
  }

  if (failed) {
    // Deliberately uniform: unknown, revoked and deleted all read the same, so
    // a visitor cannot learn that a token was ever real.
    return <div role="alert">This dashboard is no longer available.</div>;
  }

  if (!dashboard) return <div aria-busy="true">Loading…</div>;

  const src =
    `${RENDER_ORIGIN}/d/${encodeURIComponent(dashboard.id)}` +
    `?v=${encodeURIComponent(dashboard.version)}`;

  return (
    <iframe
      ref={frameRef}
      src={src}
      title={dashboard.title}
      onLoad={onFrameLoad}
      /* allow-scripts ONLY, as in the authenticated view. */
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      style={{ display: 'block', width: '100%', height, border: 0, background: 'transparent' }}
    />
  );
}
